import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { KsgPopover, KsgAvatar, KsgIcon } from '@ksg/ui';
import { AccountantCompaniesService, AssignedCompany } from '../../../core/company/accountant-companies.service';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-company-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, KsgPopover, KsgAvatar, KsgIcon],
  template: `
    <div class="company-switcher">
      <button
        #trigger
        type="button"
        class="company-switcher-trigger"
        (click)="open.set(!open())"
        [attr.aria-label]="'company.switch' | transloco"
      >
        @if (companiesService.selectedCompany(); as company) {
          <ksg-avatar [label]="companyInitials(company.name)" shape="circle" size="md" />
          @if (!collapsed()) {
            <div class="company-switcher-info">
              <span class="company-name">{{ company.name }}</span>
              @if (company.nip) {
                <span class="company-nip">NIP: {{ company.nip }}</span>
              }
            </div>
            <ksg-icon name="pi-chevron-up" class="company-switcher-chevron" [size]="16" />
          }
        } @else {
          <ksg-avatar icon="pi-building" shape="circle" size="md" />
          @if (!collapsed()) {
            <span class="company-name">{{ 'company.select' | transloco }}</span>
          }
        }
      </button>

      <ksg-popover [anchor]="trigger" [(open)]="open" position="top" [ariaLabel]="'company.switch' | transloco">
        <div class="company-switcher-panel">
          @if (companiesService.companies().length > 0) {
            <p class="company-panel-label">{{ 'company.myCompanies' | transloco }}</p>
            <ul class="company-list" role="listbox" [attr.aria-label]="'company.myCompanies' | transloco">
              @for (company of companiesService.companies(); track company.id) {
                <li
                  class="company-list-item"
                  [class.selected]="company.id === companiesService.selectedCompanyId()"
                  role="option"
                  [attr.aria-selected]="company.id === companiesService.selectedCompanyId()"
                  (click)="select(company)"
                  (keydown.enter)="select(company)"
                  (keydown.space)="select(company)"
                  tabindex="0"
                >
                  <ksg-avatar [label]="companyInitials(company.name)" shape="circle" size="md" />
                  <div class="company-item-info">
                    <span class="company-item-name">{{ company.name }}</span>
                    @if (company.nip) {
                      <span class="company-item-nip">NIP: {{ company.nip }}</span>
                    }
                  </div>
                  @if (company.id === companiesService.selectedCompanyId()) {
                    <ksg-icon name="pi-check" class="company-item-check" [size]="18" />
                  }
                </li>
              }
            </ul>
          } @else {
            <p class="company-panel-empty">{{ 'company.empty' | transloco }}</p>
          }
          <hr class="company-panel-sep" />
          <button type="button" class="company-logout-btn" (click)="logout()">
            <ksg-icon name="pi-sign-out" [size]="18" />
            <span>{{ 'auth.logout' | transloco }}</span>
          </button>
        </div>
      </ksg-popover>
    </div>
  `,
})
export class CompanySwitcher implements OnInit {
  readonly companiesService = inject(AccountantCompaniesService);
  private readonly authService = inject(AuthService);

  readonly collapsed = input(false, { transform: booleanAttribute });

  protected readonly open = signal(false);

  ngOnInit(): void {
    if (this.authService.isAuthenticated() && !this.companiesService.loaded()) {
      this.companiesService.loadCompanies().subscribe({ error: () => {} });
    }
  }

  select(company: AssignedCompany): void {
    this.companiesService.selectCompany(company.id);
    this.open.set(false);
  }

  logout(): void {
    this.authService.logout();
  }

  companyInitials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase();
  }
}
